import React, { useState, useEffect } from 'react';
import { 
  Settings, DollarSign, CreditCard, AlertTriangle, 
  Save, RefreshCw, Shield, Mail, Phone, Building2,
  Percent, Wallet, ToggleLeft, ToggleRight, CheckCircle, XCircle
} from 'lucide-react';
import adminService from '../../services/admin/adminService';
import { 
  AppSettings, 
  CommissionSettings, 
  PaymentGatewayConfig,
  UpdateAppSettingsRequest,
  UpdateCommissionSettingsRequest,
  UpdatePaymentGatewayConfigRequest,
  ToggleEmergencyShutdownRequest
} from '../../services/admin/adminService';
import toast from 'react-hot-toast';

type TabType = 'general' | 'commission' | 'payment-gateway' | 'emergency';

const AppConfiguration: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('general');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // General Settings State
  const [appSettings, setAppSettings] = useState<AppSettings | null>(null);
  const [generalForm, setGeneralForm] = useState({
    platform_name: '',
    platform_email: '',
    platform_phone: '',
    support_email: '',
    support_phone: '',
    minimum_wallet_balance: ''
  });

  // Commission Settings State
  const [commissionSettings, setCommissionSettings] = useState<CommissionSettings | null>(null);
  const [commissionForm, setCommissionForm] = useState({
    commission_rate: '',
    commission_type: 'percentage' as 'percentage' | 'fixed',
    minimum_commission: '',
    maximum_commission: '',
    tax_percentage: '',
    apply_tax_on_commission: false
  });

  // Payment Gateway State
  const [paymentGatewayConfig, setPaymentGatewayConfig] = useState<PaymentGatewayConfig | null>(null);
  const [gatewayForm, setGatewayForm] = useState({
    gateway_name: 'razorpay' as 'stripe' | 'razorpay' | 'paypal',
    api_key: '',
    api_secret: '',
    webhook_secret: '',
    is_active: true
  });
  const [showSecrets, setShowSecrets] = useState(false);

  // Emergency Shutdown State
  const [emergencyShutdown, setEmergencyShutdown] = useState({
    enabled: false,
    reason: ''
  });

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadAppSettings(),
        loadCommissionSettings(),
        loadPaymentGatewayConfig()
      ]);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const loadAppSettings = async () => {
    try {
      const settings = await adminService.getAppSettings();
      setAppSettings(settings);
      setGeneralForm({
        platform_name: settings.platform_name || '',
        platform_email: settings.platform_email || '',
        platform_phone: settings.platform_phone || '',
        support_email: settings.support_email || '',
        support_phone: settings.support_phone || '',
        minimum_wallet_balance: settings.minimum_wallet_balance || ''
      });
      setEmergencyShutdown({
        enabled: settings.emergency_shutdown === 1,
        reason: settings.emergency_shutdown_reason || ''
      });
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load app settings');
    }
  };

  const loadCommissionSettings = async () => {
    try {
      const settings = await adminService.getCommissionSettings();
      setCommissionSettings(settings);
      setCommissionForm({
        commission_rate: settings.commission_rate || '',
        commission_type: settings.commission_type || 'percentage',
        minimum_commission: settings.minimum_commission || '',
        maximum_commission: settings.maximum_commission || '',
        tax_percentage: '',
        apply_tax_on_commission: false
      });
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load commission settings');
    }
  };

  const loadPaymentGatewayConfig = async () => {
    try {
      const config = await adminService.getPaymentGatewayConfig();
      setPaymentGatewayConfig(config);
      setGatewayForm({
        gateway_name: config.gateway_name,
        api_key: config.api_key || '',
        api_secret: '',
        webhook_secret: '',
        is_active: config.is_active
      });
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load payment gateway config');
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadAllData();
    setRefreshing(false);
    toast.success('Configuration refreshed');
  };

  const handleSaveGeneralSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const data: UpdateAppSettingsRequest = {
        platform_name: generalForm.platform_name,
        platform_email: generalForm.platform_email,
        platform_phone: generalForm.platform_phone || undefined,
        support_email: generalForm.support_email,
        support_phone: generalForm.support_phone || undefined,
        minimum_wallet_balance: generalForm.minimum_wallet_balance ? parseFloat(generalForm.minimum_wallet_balance) : undefined
      };
      
      await adminService.updateAppSettings(data);
      await loadAppSettings();
      toast.success('General settings updated successfully');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update general settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCommissionSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const data: UpdateCommissionSettingsRequest = {
        platform_commission_percentage: parseFloat(commissionForm.commission_rate),
        commission_type: commissionForm.commission_type,
        minimum_commission: commissionForm.minimum_commission ? parseFloat(commissionForm.minimum_commission) : undefined,
        maximum_commission: commissionForm.maximum_commission ? parseFloat(commissionForm.maximum_commission) : undefined,
        tax_percentage: commissionForm.tax_percentage ? parseFloat(commissionForm.tax_percentage) : undefined,
        apply_tax_on_commission: commissionForm.apply_tax_on_commission
      };
      
      await adminService.updateCommissionSettings(data);
      await loadCommissionSettings();
      toast.success('Commission settings updated successfully');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update commission settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePaymentGateway = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const data: UpdatePaymentGatewayConfigRequest = {
        gateway_name: gatewayForm.gateway_name,
        api_key: gatewayForm.api_key || undefined,
        api_secret: gatewayForm.api_secret || undefined,
        webhook_secret: gatewayForm.webhook_secret || undefined,
        is_active: gatewayForm.is_active
      };
      
      await adminService.updatePaymentGatewayConfig(data);
      await loadPaymentGatewayConfig();
      toast.success('Payment gateway configuration updated successfully');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update payment gateway config');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEmergencyShutdown = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (emergencyShutdown.enabled && !emergencyShutdown.reason.trim()) {
      toast.error('Please provide a reason for enabling emergency shutdown');
      return;
    }
    
    setSaving(true);
    
    try {
      const data: ToggleEmergencyShutdownRequest = {
        enabled: emergencyShutdown.enabled,
        reason: emergencyShutdown.reason || undefined
      };
      
      const response = await adminService.toggleEmergencyShutdown(data);
      setEmergencyShutdown({
        enabled: response.emergency_shutdown === 1,
        reason: response.emergency_shutdown_reason || ''
      });
      toast.success(`Emergency shutdown ${emergencyShutdown.enabled ? 'enabled' : 'disabled'} successfully`);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to toggle emergency shutdown');
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'general' as TabType, label: 'General Settings', icon: Settings },
    { id: 'commission' as TabType, label: 'Commission', icon: DollarSign },
    { id: 'payment-gateway' as TabType, label: 'Payment Gateway', icon: CreditCard },
    { id: 'emergency' as TabType, label: 'Emergency Shutdown', icon: AlertTriangle }
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <RefreshCw className="animate-spin text-primary-500" size={32} />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900">App Configuration</h1>
          <p className="text-secondary-600 mt-1">Manage platform settings, commission, and payment gateway</p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2 disabled:opacity-50"
        >
          <RefreshCw className={refreshing ? 'animate-spin' : ''} size={18} />
          Refresh
        </button>
      </div>

      {/* Emergency Shutdown Banner */}
      {appSettings && appSettings.emergency_shutdown === 1 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertTriangle className="text-red-600" size={24} />
          <div className="flex-1">
            <h3 className="font-semibold text-red-900">Emergency Shutdown Active</h3>
            <p className="text-sm text-red-700">
              {appSettings.emergency_shutdown_reason || 'Platform is currently in emergency shutdown mode'}
            </p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="flex border-b border-gray-200">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex-1 px-6 py-4 flex items-center justify-center gap-2 transition-colors ${
                activeTab === id
                  ? 'border-b-2 border-primary-500 text-primary-600 font-semibold bg-primary-50'
                  : 'text-secondary-600 hover:bg-gray-50'
              }`}
            >
              <Icon size={20} />
              {label}
            </button>
          ))}
        </div>

        <div className="p-6">
          {/* General Settings Tab */}
          {activeTab === 'general' && (
            <form onSubmit={handleSaveGeneralSettings} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    <Building2 className="inline mr-2" size={16} />
                    Platform Name
                  </label>
                  <input
                    type="text"
                    value={generalForm.platform_name}
                    onChange={(e) => setGeneralForm({ ...generalForm, platform_name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    <Mail className="inline mr-2" size={16} />
                    Platform Email
                  </label>
                  <input
                    type="email"
                    value={generalForm.platform_email}
                    onChange={(e) => setGeneralForm({ ...generalForm, platform_email: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    <Phone className="inline mr-2" size={16} />
                    Platform Phone
                  </label>
                  <input
                    type="tel"
                    value={generalForm.platform_phone}
                    onChange={(e) => setGeneralForm({ ...generalForm, platform_phone: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="+91 1234567890"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    <Mail className="inline mr-2" size={16} />
                    Support Email
                  </label>
                  <input
                    type="email"
                    value={generalForm.support_email}
                    onChange={(e) => setGeneralForm({ ...generalForm, support_email: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    <Phone className="inline mr-2" size={16} />
                    Support Phone
                  </label>
                  <input
                    type="tel"
                    value={generalForm.support_phone}
                    onChange={(e) => setGeneralForm({ ...generalForm, support_phone: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="+91 1234567890"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    <Wallet className="inline mr-2" size={16} />
                    Minimum Wallet Balance ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={generalForm.minimum_wallet_balance}
                    onChange={(e) => setGeneralForm({ ...generalForm, minimum_wallet_balance: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="100.00"
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  <Save size={18} />
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          )}

          {/* Commission Settings Tab */}
          {activeTab === 'commission' && (
            <form onSubmit={handleSaveCommissionSettings} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    <Percent className="inline mr-2" size={16} />
                    Commission Rate (%)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={commissionForm.commission_rate}
                    onChange={(e) => setCommissionForm({ ...commissionForm, commission_rate: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  />
                  <p className="text-xs text-secondary-500 mt-1">Current: {commissionSettings?.commission_rate}%</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Commission Type
                  </label>
                  <select
                    value={commissionForm.commission_type}
                    onChange={(e) => setCommissionForm({ ...commissionForm, commission_type: e.target.value as 'percentage' | 'fixed' })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed Amount</option>
                  </select>
                  <p className="text-xs text-secondary-500 mt-1">Current: {commissionSettings?.commission_type}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Minimum Commission ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={commissionForm.minimum_commission}
                    onChange={(e) => setCommissionForm({ ...commissionForm, minimum_commission: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <p className="text-xs text-secondary-500 mt-1">Current: ${commissionSettings?.minimum_commission}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Maximum Commission ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={commissionForm.maximum_commission}
                    onChange={(e) => setCommissionForm({ ...commissionForm, maximum_commission: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <p className="text-xs text-secondary-500 mt-1">Current: ${commissionSettings?.maximum_commission}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Tax Percentage (%)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={commissionForm.tax_percentage}
                    onChange={(e) => setCommissionForm({ ...commissionForm, tax_percentage: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="18.00"
                  />
                </div>

                <div className="flex items-center">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={commissionForm.apply_tax_on_commission}
                      onChange={(e) => setCommissionForm({ ...commissionForm, apply_tax_on_commission: e.target.checked })}
                      className="w-4 h-4 text-primary-500 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm font-medium text-secondary-700">Apply Tax on Commission</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  <Save size={18} />
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          )}

          {/* Payment Gateway Tab */}
          {activeTab === 'payment-gateway' && (
            <form onSubmit={handleSavePaymentGateway} className="space-y-6">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Payment Gateway
                  </label>
                  <select
                    value={gatewayForm.gateway_name}
                    onChange={(e) => setGatewayForm({ ...gatewayForm, gateway_name: e.target.value as 'stripe' | 'razorpay' | 'paypal' })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="razorpay">Razorpay</option>
                    <option value="stripe">Stripe</option>
                    <option value="paypal">PayPal</option>
                  </select>
                  <p className="text-xs text-secondary-500 mt-1">Current: {paymentGatewayConfig?.gateway_name}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    API Key
                  </label>
                  <input
                    type={showSecrets ? 'text' : 'password'}
                    value={gatewayForm.api_key}
                    onChange={(e) => setGatewayForm({ ...gatewayForm, api_key: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Enter API key"
                  />
                  {paymentGatewayConfig?.api_key && (
                    <p className="text-xs text-secondary-500 mt-1">
                      Current: {showSecrets ? paymentGatewayConfig.api_key : '••••••••' + paymentGatewayConfig.api_key.slice(-4)}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    API Secret
                  </label>
                  <input
                    type={showSecrets ? 'text' : 'password'}
                    value={gatewayForm.api_secret}
                    onChange={(e) => setGatewayForm({ ...gatewayForm, api_secret: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Enter API secret (leave blank to keep current)"
                  />
                  <p className="text-xs text-secondary-500 mt-1">Leave blank to keep current secret</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Webhook Secret
                  </label>
                  <input
                    type={showSecrets ? 'text' : 'password'}
                    value={gatewayForm.webhook_secret}
                    onChange={(e) => setGatewayForm({ ...gatewayForm, webhook_secret: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Enter webhook secret (leave blank to keep current)"
                  />
                  <p className="text-xs text-secondary-500 mt-1">Leave blank to keep current secret</p>
                </div>

                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={gatewayForm.is_active}
                      onChange={(e) => setGatewayForm({ ...gatewayForm, is_active: e.target.checked })}
                      className="w-4 h-4 text-primary-500 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm font-medium text-secondary-700">Gateway Active</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showSecrets}
                      onChange={(e) => setShowSecrets(e.target.checked)}
                      className="w-4 h-4 text-primary-500 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm font-medium text-secondary-700">Show Secrets</span>
                  </label>
                </div>

                {paymentGatewayConfig && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-secondary-600">
                      <strong>Status:</strong>{' '}
                      <span className={paymentGatewayConfig.is_active ? 'text-green-600' : 'text-red-600'}>
                        {paymentGatewayConfig.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </p>
                  </div>
                )}
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  <Save size={18} />
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          )}

          {/* Emergency Shutdown Tab */}
          {activeTab === 'emergency' && (
            <form onSubmit={handleToggleEmergencyShutdown} className="space-y-6">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="text-yellow-600 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold text-yellow-900 mb-1">Emergency Shutdown</h3>
                    <p className="text-sm text-yellow-700">
                      Enabling emergency shutdown will temporarily disable all platform operations. 
                      This should only be used in critical situations.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h3 className="font-semibold text-secondary-900">Emergency Shutdown Status</h3>
                    <p className="text-sm text-secondary-600 mt-1">
                      {emergencyShutdown.enabled 
                        ? 'Platform is currently in emergency shutdown mode' 
                        : 'Platform is operating normally'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {emergencyShutdown.enabled ? (
                      <XCircle className="text-red-600" size={24} />
                    ) : (
                      <CheckCircle className="text-green-600" size={24} />
                    )}
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      emergencyShutdown.enabled 
                        ? 'bg-red-100 text-red-800' 
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {emergencyShutdown.enabled ? 'Shutdown Active' : 'Normal Operation'}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="flex items-center gap-2 cursor-pointer p-4 bg-gray-50 rounded-lg">
                    {emergencyShutdown.enabled ? (
                      <ToggleRight className="text-red-600" size={32} />
                    ) : (
                      <ToggleLeft className="text-gray-400" size={32} />
                    )}
                    <div className="flex-1">
                      <span className="font-medium text-secondary-900 block">
                        {emergencyShutdown.enabled ? 'Disable Emergency Shutdown' : 'Enable Emergency Shutdown'}
                      </span>
                      <span className="text-sm text-secondary-600">
                        {emergencyShutdown.enabled 
                          ? 'Click to restore normal platform operations' 
                          : 'Click to activate emergency shutdown mode'}
                      </span>
                    </div>
                    <input
                      type="checkbox"
                      checked={emergencyShutdown.enabled}
                      onChange={(e) => setEmergencyShutdown({ ...emergencyShutdown, enabled: e.target.checked })}
                      className="sr-only"
                    />
                  </label>
                </div>

                {emergencyShutdown.enabled && (
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Reason for Shutdown <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={emergencyShutdown.reason}
                      onChange={(e) => setEmergencyShutdown({ ...emergencyShutdown, reason: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      rows={4}
                      placeholder="Explain why emergency shutdown is being activated..."
                      required
                    />
                  </div>
                )}

                {!emergencyShutdown.enabled && emergencyShutdown.reason && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-secondary-600">
                      <strong>Last Shutdown Reason:</strong> {emergencyShutdown.reason}
                    </p>
                  </div>
                )}
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={saving}
                  className={`px-6 py-2 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 ${
                    emergencyShutdown.enabled
                      ? 'bg-green-500 text-white hover:bg-green-600'
                      : 'bg-red-500 text-white hover:bg-red-600'
                  }`}
                >
                  <Shield size={18} />
                  {saving 
                    ? 'Processing...' 
                    : emergencyShutdown.enabled 
                      ? 'Disable Shutdown' 
                      : 'Enable Shutdown'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AppConfiguration;

